import { useQuery, type QueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from './api';
import type { Building, Category, Room } from './types';

export function useDebouncedValue<T>(value: T, delayMs = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/**
 * List filters that live in the URL, so they survive reloads and can be shared.
 * Values equal to their default are omitted from the URL.
 */
export function useUrlState<T extends Record<string, string>>(defaults: T) {
  const [params, setParams] = useSearchParams();
  const state = useMemo(() => {
    const next = { ...defaults };
    for (const key of Object.keys(defaults) as (keyof T)[]) {
      const value = params.get(key as string);
      if (value !== null) next[key] = value as T[keyof T];
    }
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const update = useCallback(
    (patch: Partial<T>, options: { resetPage?: boolean } = { resetPage: true }) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          const merged: Partial<T> = { ...patch };
          if (options.resetPage && !('page' in patch) && 'page' in defaults) (merged as Record<string, string>).page = defaults.page;
          for (const [key, value] of Object.entries(merged)) {
            if (value === undefined || value === '' || value === defaults[key]) next.delete(key);
            else next.set(key, String(value));
          }
          return next;
        },
        { replace: true },
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParams],
  );

  return [state, update] as const;
}

export const useCategories = () =>
  useQuery({ queryKey: ['categories'], queryFn: () => api.get<Category[]>('/categories'), staleTime: 5 * 60_000 });

export const useBuildings = () =>
  useQuery({ queryKey: ['buildings'], queryFn: () => api.get<Building[]>('/buildings'), staleTime: 60_000 });

export const useRooms = () =>
  useQuery({ queryKey: ['rooms', 'all'], queryFn: () => api.get<Room[]>('/rooms'), staleTime: 60_000 });

/** Refreshes everything that shows assignment state after a check-in/out or asset change. */
export function invalidateInventory(queryClient: QueryClient) {
  for (const key of [
    'assets',
    'asset',
    'checkouts',
    'dashboard',
    'people',
    'person',
    'rooms',
    'room',
    'buildings',
    'google-devices',
    'google-device',
    'managed-devices',
    'managed-device',
    'categories',
    'work-orders',
    'work-order',
    'my-devices',
    'catalog',
    'parts',
    'part',
    'part-assignments',
    'work-order-parts',
    'work-order-related',
    'work-order-automation',
    'departments',
    'workflows',
    'workflow',
    'automation-runs',
    'smart-rules',
    'recurring-tickets',
    'helpdesk-team',
    'notifications',
    'carts',
    'cart',
    'room-assignments',
    'asset-locations',
    'facilities',
    'loaners',
    'loaner-options',
    'borrower-alerts',
    'person-fields',
    'checkout-grades',
    'checkout-rollout',
    'checkout-returns',
  ]) {
    void queryClient.invalidateQueries({ queryKey: [key] });
  }
}

/** June 15 of the current school year (school years start in July). */
export function endOfSchoolYear(now = new Date()) {
  const year = now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
  return new Date(year, 5, 15);
}
